import { useState, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pencil, Save, X, Loader2, Check } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import { useAuth } from '@shared/context/useAuth';
import type { ClinicServiceFee, ClinicProcedureDto } from '../types/clinic';
import './ClinicServicesEditor.css';

interface ClinicProceduresViewProps {
  clinicId: number;
}

interface GroupedSection {
  groupId: number;
  groupName: string;
  displayOrder: number;
  services: ClinicServiceFee[];
  activeCount: number;
  totalMapped: number;
}

interface EditRowState {
  isAvailable: boolean;
  /** SLV2-3452: admin-only fee text; blank = keep the fee on file. */
  fee: string;
}

/** SLV2-3452: fee entry is admin-only — the same Admin / SysAdmin rule the API enforces. */
function canEditFees(roles: string[] | undefined): boolean {
  return !!roles && (roles.includes('Admin') || roles.includes('SysAdmin'));
}

function parseFee(text: string): number | null | 'invalid' {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && n <= 999999.99 ? Math.round(n * 100) / 100 : 'invalid';
}

export default function ClinicProceduresView({ clinicId }: Readonly<ClinicProceduresViewProps>) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isFeeAdmin = canEditFees(user?.roles);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editState, setEditState] = useState<Record<number, EditRowState>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: masterGroups, isLoading: masterLoading } = useQuery({
    queryKey: ['clinic-services-master', clinicId],
    queryFn: () => clinicApi.getClinicServicesMaster(clinicId),
    enabled: clinicId > 0,
  });

  const { data: services, isLoading: servicesLoading, error } = useQuery({
    queryKey: ['clinic-services', clinicId],
    queryFn: () => clinicApi.getClinicServices(clinicId),
    enabled: clinicId > 0,
  });

  const isLoading = masterLoading || servicesLoading;

  const saveMutation = useMutation({
    mutationFn: (groupId: number) => {
      // SLV2-3452: admins send the fee they typed (blank = keep); everyone else sends no fee, so the
      // fee on file is kept. Sending 0 here used to overwrite real fees.
      const groupServices = Object.entries(editState)
        .filter(([, v]) => v.isAvailable)
        .map(([procId, v]) => {
          const parsed = isFeeAdmin ? parseFee(v.fee) : null;
          return { procedureId: Number(procId), fee: parsed === 'invalid' ? null : parsed };
        });
      return clinicApi.updateClinicServices(clinicId, {
        serviceGroupId: groupId,
        services: groupServices,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-services-master', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['clinic-services', clinicId] });
      queryClient.invalidateQueries({ queryKey: ['clinicFees', clinicId] });
      exitEditMode();
      setSaveError(null);
      showSuccess('Procedures updated');
    },
    onError: (err: Error) => {
      setSaveError(err.message || 'Failed to save procedures');
    },
  });

  const showSuccess = (msg: string) => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    setSuccessMessage(msg);
    successTimerRef.current = setTimeout(() => setSuccessMessage(null), 3000);
  };

  const groupedSections = useMemo((): GroupedSection[] => {
    if (!masterGroups) return [];

    const sections: GroupedSection[] = [];

    for (const group of masterGroups) {
      const matchedServices = services
        ? services.filter((svc) =>
            group.procedures.some((p) => p.procedureId === svc.procedureId && p.isAvailable)
          )
        : [];

      const sorted = [...matchedServices].sort((a, b) =>
        a.procedureName.localeCompare(b.procedureName)
      );

      sections.push({
        groupId: group.serviceGroupId,
        groupName: group.groupName,
        displayOrder: group.displayOrder,
        services: sorted,
        activeCount: matchedServices.length,
        totalMapped: group.procedures.length,
      });
    }

    sections.sort((a, b) => a.displayOrder - b.displayOrder);
    return sections;
  }, [masterGroups, services]);

  const enterEditMode = useCallback((groupId: number) => {
    if (!masterGroups) return;
    const group = masterGroups.find((g) => g.serviceGroupId === groupId);
    if (!group) return;

    const state: Record<number, EditRowState> = {};
    for (const proc of group.procedures) {
      state[proc.procedureId] = {
        isAvailable: proc.isAvailable,
        fee: proc.fee != null ? proc.fee.toFixed(2) : '',
      };
    }
    setEditState(state);
    setEditingGroupId(groupId);
    setIsEditMode(true);
    setSaveError(null);
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  }, [masterGroups]);

  const exitEditMode = () => {
    setIsEditMode(false);
    setEditingGroupId(null);
    setEditState({});
    setSaveError(null);
  };

  const handleEditClick = (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditMode && editingGroupId !== groupId) {
      exitEditMode();
      setTimeout(() => enterEditMode(groupId), 0);
    } else {
      enterEditMode(groupId);
    }
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFeeAdmin) {
      const bad = Object.values(editState).some((v) => v.isAvailable && parseFee(v.fee) === 'invalid');
      if (bad) { setSaveError('Enter each fee as a number between 0 and 999,999.99, or leave it blank to keep the current fee.'); return; }
    }
    if (editingGroupId != null) {
      saveMutation.mutate(editingGroupId);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    exitEditMode();
  };

  const handleToggle = (procedureId: number) => {
    setEditState((prev) => {
      const current = prev[procedureId];
      return {
        ...prev,
        [procedureId]: {
          ...current,
          isAvailable: !current.isAvailable,
        },
      };
    });
  };

  const handleFeeChange = (procedureId: number, value: string) => {
    setEditState((prev) => ({ ...prev, [procedureId]: { ...prev[procedureId], fee: value } }));
  };

  const toggleGroup = (groupId: number) => {
    if (isEditMode && editingGroupId === groupId) return;
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  if (!clinicId || clinicId <= 0) {
    return <div className="services-editor"><p>No clinic selected.</p></div>;
  }

  if (isLoading) {
    return (
      <div className="services-editor">
        <div className="services-editor-loading">
          <Loader2 className="spin" size={20} />
          Loading procedures...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="services-editor">
        <div className="services-editor-error">Error loading procedures</div>
      </div>
    );
  }

  if (!masterGroups || masterGroups.length === 0) {
    return (
      <div className="services-editor">
        <p className="services-editor-empty">No procedures configured for this clinic</p>
      </div>
    );
  }

  const totalActive = groupedSections.reduce((sum, g) => sum + g.activeCount, 0);

  const renderGroupSections = () => {
    const sections: React.ReactNode[] = [];

    for (const section of groupedSections) {
      if (isEditMode && editingGroupId === section.groupId) {
        sections.push(renderEditingGroup(section.groupId));
      } else if (section.services.length > 0) {
        sections.push(renderReadOnlyGroup(section));
      } else {
        sections.push(renderEmptyGroup(section));
      }
    }

    return sections;
  };

  const renderEmptyGroup = (section: GroupedSection) => {
    const isCollapsed = collapsedGroups.has(section.groupId);
    return (
      <div key={section.groupId} className="service-group-section">
        <div className="service-group-header" onClick={() => toggleGroup(section.groupId)}>
          <span className="service-group-chevron">
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
          <span className="service-group-name">{section.groupName}</span>
          <span className="service-group-badge">0</span>
          <button
            type="button"
            className="svc-edit-btn"
            onClick={(e) => handleEditClick(section.groupId, e)}
            title="Edit procedures"
          >
            <Pencil size={12} />
            Edit (0/{section.totalMapped})
          </button>
        </div>
        {!isCollapsed && (
          <div className="service-group-body">
            <div className="service-row service-row-empty">
              No procedures active
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderReadOnlyGroup = (section: GroupedSection) => {
    const isCollapsed = collapsedGroups.has(section.groupId);
    return (
      <div key={section.groupId} className="service-group-section">
        <div className="service-group-header" onClick={() => toggleGroup(section.groupId)}>
          <span className="service-group-chevron">
            {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
          <span className="service-group-name">{section.groupName}</span>
          <span className="service-group-badge">{section.activeCount}</span>
          <button
            type="button"
            className="svc-edit-btn"
            onClick={(e) => handleEditClick(section.groupId, e)}
            title="Edit procedures"
          >
            <Pencil size={12} />
            Edit ({section.activeCount}/{section.totalMapped})
          </button>
        </div>
        {!isCollapsed && (
          <div className="service-group-body">
            <div className="service-row service-row-header">
              <div className="service-col-status">Status</div>
              <div className="service-col-name">Procedure</div>
              <div className="service-col-code">Code</div>
              {isFeeAdmin && <div className="service-col-fee">Fee</div>}
            </div>
            {section.services.map((svc) => (
              <div key={svc.vendorFeeId} className="service-row">
                <div className="service-col-status">
                  <span className="svc-status-badge svc-status-active">
                    <Check size={12} />
                    Active
                  </span>
                </div>
                <div className="service-col-name">{svc.procedureName}</div>
                <div className="service-col-code">{svc.procedureCode}</div>
                {isFeeAdmin && <div className="service-col-fee">{svc.fee != null ? `$${svc.fee.toFixed(2)}` : '-'}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderEditingGroup = (groupId: number) => {
    const masterGroup = masterGroups?.find((g) => g.serviceGroupId === groupId);
    if (!masterGroup) return null;

    return (
      <div key={groupId} className="service-group-section service-group-editing">
        <div className="service-group-header service-group-header-editing">
          <span className="service-group-chevron">
            <ChevronDown size={16} />
          </span>
          <span className="service-group-name">{masterGroup.groupName}</span>
          <div className="svc-header-actions">
            <button
              type="button"
              className="svc-header-btn svc-header-btn-cancel"
              onClick={handleCancel}
              disabled={saveMutation.isPending}
            >
              <X size={13} />
              Cancel
            </button>
            <button
              type="button"
              className="svc-header-btn svc-header-btn-save"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Loader2 className="spin" size={13} />
              ) : (
                <Save size={13} />
              )}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {saveError && (
          <div className="services-editor-save-error">{saveError}</div>
        )}

        <div className="service-group-body">
          <div className="service-row service-row-header">
            <div className="service-col-toggle">Active</div>
            <div className="service-col-name">Procedure</div>
            {isFeeAdmin && <div className="service-col-fee">Fee</div>}
          </div>
          {masterGroup.procedures.map((proc) => (
            <EditableRow
              key={proc.procedureId}
              proc={proc}
              editValue={editState[proc.procedureId]}
              onToggle={() => handleToggle(proc.procedureId)}
              showFee={isFeeAdmin}
              onFeeChange={(val) => handleFeeChange(proc.procedureId, val)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="services-editor">
      <div className="services-editor-toolbar">
        <div className="services-editor-summary">
          {totalActive} {totalActive === 1 ? 'procedure' : 'procedures'} enabled
        </div>
        {successMessage && (
          <div className="svc-success-toast">
            <Check size={14} />
            {successMessage}
          </div>
        )}
      </div>

      <div className="services-editor-grid">
        {renderGroupSections()}
      </div>
    </div>
  );
}

interface EditableRowProps {
  proc: ClinicProcedureDto;
  editValue?: EditRowState;
  onToggle: () => void;
  /** SLV2-3452: admins only */
  showFee: boolean;
  onFeeChange: (value: string) => void;
}

function EditableRow({ proc, editValue, onToggle, showFee, onFeeChange }: Readonly<EditableRowProps>) {
  const isAvailable = editValue?.isAvailable ?? false;

  return (
    <div className={`service-row${!isAvailable ? ' service-row-inactive' : ''}`}>
      <div className="service-col-toggle">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={isAvailable}
            onChange={onToggle}
            title={`Toggle ${proc.procedureName}`}
          />
          <span className="toggle-slider" />
        </label>
      </div>
      <div className="service-col-name">
        {proc.procedureName}
      </div>
      {showFee && (
        <div className="service-col-fee">
          <div className="fee-input-wrapper">
            <span className="fee-input-prefix">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="fee-input"
              value={editValue?.fee ?? ''}
              onChange={(e) => onFeeChange(e.target.value)}
              disabled={!isAvailable}
              placeholder={isAvailable ? 'keep' : ''}
              aria-label={`Fee for ${proc.procedureName}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
